import { Link } from 'react-router-dom';

interface ArticleCardProps {
  article: {
    slug: string;
    title: string;
    excerpt: string;
    category: string;
    image: string;
    author: string;
    publishedAt: string;
    readTime: string;
    viewCount: number;
    tags: string[];
    isHot?: boolean;
    isNew?: boolean;
  };
  variant?: 'default' | 'featured' | 'compact';
}

const categoryNameMap: Record<string, string> = {
  'wanghong-fanche': '网红翻车',
  'yule-bagua': '娱乐八卦',
  'heila-baoliao': '黑料爆料',
  'jieri-dagua': '今日大瓜',
  'zhibo-fanche': '直播翻车',
  'shejiao-reshi': '社交热事',
};

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const categoryName = categoryNameMap[article.category] || article.category;

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative rounded-lg overflow-hidden cursor-pointer h-[420px] md:h-[500px]"
      >
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 吃瓜官网独家爆料`}
          className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
          <div className="flex items-center gap-2 mb-3">
            {article.isHot && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                <i className="ri-fire-line text-xs"></i> 热门
              </span>
            )}
            {article.isNew && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                <i className="ri-sparkling-line text-xs"></i> 最新
              </span>
            )}
            <span className="text-background-50/80 text-xs">{article.publishedAt}</span>
          </div>
          <h2 className="font-heading font-bold text-xl md:text-3xl text-background-50 mb-2 group-hover:text-primary-300 transition-colors line-clamp-2">
            {article.title}
          </h2>
          <p className="text-background-50/60 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-background-50/50 text-xs">
            <span><i className="ri-user-line mr-1"></i>{article.author}</span>
            <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
            <span><i className="ri-eye-line mr-1"></i>{(article.viewCount / 10000).toFixed(0)}万阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 吃瓜官网`}
          className="w-20 h-20 rounded-md object-cover object-top flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 text-foreground-400 text-xs">
            <span>{article.publishedAt}</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300">
      <Link to={`/article/${article.slug}`} className="block cursor-pointer">
        <div className="relative overflow-hidden h-48">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 吃瓜官网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            {(article.isHot || article.isNew) && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-background-50 ${article.isHot ? 'bg-accent-500' : 'bg-primary-500'}`}>
                {article.isHot ? '热门' : '最新'}
              </span>
            )}
          </div>
        </div>
      </Link>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-primary-600 font-medium bg-primary-100 px-2 py-0.5 rounded-full">
            {categoryName}
          </span>
          <span className="text-xs text-foreground-400">{article.publishedAt}</span>
        </div>
        <Link to={`/article/${article.slug}`} className="cursor-pointer">
          <h3 className="font-heading font-bold text-base text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
        </Link>
        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span><i className="ri-user-line mr-1"></i>{article.author}</span>
          <div className="flex items-center gap-2">
            <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
            <span><i className="ri-eye-line mr-1"></i>{(article.viewCount / 10000).toFixed(0)}万</span>
          </div>
        </div>
      </div>
    </article>
  );
}